//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  DimensionValidationRules,
  VariationValidationRules,
} from '@campaigns/types/sampling/SamplingProductValidationTypes';
import { validateNumber } from '@campaigns/utils/FormUtils';
import { isDefined } from '@utils/filterPredicates';
import { URL_REGEX_PROTOCOL_ONLY } from '@utils/urlValidation';

export const URL_REGEX_RULE = new RegExp(URL_REGEX_PROTOCOL_ONLY);

/**
 * Regular expression to validate product IDs.
 *
 * This regex ensures that:
 * - The string does not start with a hyphen (-).
 * - The string ends with a word character (alphanumeric), period (.), or hyphen (-).
 * - The string only contains word characters (alphanumeric), periods (.), hyphens (-), or pipe characters (|).
 *
 * Note: This version does not accept spaces anywhere in the string.
 *
 * Examples of valid IDs:
 * - "product-123"
 * - "item.name"
 * - "A1.B2-C3"
 * - "product|name"
 *
 * Examples of invalid IDs:
 * - " product123" (starts with a whitespace)
 * - "-product123" (starts with a hyphen)
 * - "product 123" (contains a whitespace)
 */
export const PRODUCT_ID_REGEX_RULE = /^(?=[^\-\s])(?=.*[\w.|-]$)[\w.|-]+$/;

export const isValidImageUrl = (url: string | undefined): boolean => {
  if (!isDefined(url)) {
    return false;
  }

  return URL_REGEX_RULE.test(url);
};

export const getVariationsValidationRules = (
  index: number,
  variationsLength: number,
): VariationValidationRules => {
  return {
    [`variations.${index}.fairMarketValue`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: string | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the fair market value';
        }
        if (value && Number(value) < 0) {
          return 'Fair market value must be greater than or equal to 0';
        }
      },
    },
    [`variations.${index}.imgUrl`]: {
      validate: {
        validUrl: (value: string) =>
          isValidImageUrl(value) || 'Please enter a valid URL',
      },
      required: {
        value: variationsLength > 1,
        message: 'Required',
      },
    },
    [`variations.${index}.logisticsRef`]: {
      required: {
        value: true,
        message: 'Required',
      },
    },
    [`variations.${index}.quantity`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: string | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the quantity';
        }
        if (value && Number(value) < 1) {
          return 'Quantity must be greater than 0';
        }
      },
    },
    [`variations.${index}.variantName`]: {
      required: {
        value: variationsLength > 1,
        message: 'Required',
      },
    },
  };
};

export const getProductDimensionsValidationRules = (
  number: number,
): DimensionValidationRules => {
  return {
    [`products.${number}.dimensions.format`]: {
      required: {
        value: true,
        message: 'Please select a dimension format',
      },
    },
    [`products.${number}.dimensions.depth`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the depth';
        }
        if (value && value < 0) {
          return 'Depth must be greater than or equal to 0';
        }
      },
    },
    [`products.${number}.dimensions.height`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the height';
        }
        if (value && value < 0) {
          return 'Height must be greater than or equal to 0';
        }
      },
    },
    [`products.${number}.dimensions.width`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the width';
        }
        if (value && value < 0) {
          return 'Width must be greater than or equal to 0';
        }
      },
    },
  };
};
